import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../services/authService';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState(token ? 'idle' : 'error');
  const [message, setMessage] = useState(
    token ? '' : 'This password reset link is incomplete. Request a new link and try again.',
  );

  async function handleSubmit(event) {
    event.preventDefault();
    if (!token || status === 'loading') return;
    if (newPassword.length < 8) {
      setStatus('error');
      setMessage('Your new password must contain at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus('error');
      setMessage('Your passwords do not match.');
      return;
    }

    setStatus('loading');
    setMessage('');
    try {
      const data = await resetPassword(token, newPassword);
      setStatus('success');
      setMessage(data?.message || 'Your password has been reset. You can now sign in.');
    } catch (error) {
      setStatus('error');
      setMessage(
        error.response?.data?.message ||
          'We could not reset your password. Please request a new link and try again.',
      );
    }
  }

  const canSubmit = Boolean(token) && status !== 'loading' && status !== 'success';

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/login" aria-label="Back to Court Owner Portal sign in">
            <img
              src="/freac-official-logo.png"
              alt="FREAC official logo"
              className="mx-auto h-auto w-64 object-contain"
            />
          </Link>
          <p className="mt-4 text-sm text-[#64748B] tracking-widest font-medium uppercase">
            Court Owner Portal
          </p>
        </div>

        <main className="bg-white border border-[#E5E7EB] rounded-[20px] p-8 shadow-[0_6px_18px_rgba(0,0,0,.06)]">
          <h1 className="text-2xl font-bold text-[#1F2937]">Choose a new password</h1>
          <p className="mt-2 text-sm leading-6 text-[#64748B]">
            Use at least 8 characters to keep your venue-owner account secure.
          </p>

          {status === 'success' ? (
            <div className="mt-7 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-800" role="status">
              {message}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-7 space-y-5">
              {status === 'error' && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                  {message}
                </div>
              )}
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-[#64748B] mb-1">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={!canSubmit}
                  className="input-field mt-1 disabled:bg-slate-100"
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-[#64748B] mb-1">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={!canSubmit}
                  className="input-field mt-1 disabled:bg-slate-100"
                />
              </div>
              <button type="submit" disabled={!canSubmit} className="btn-primary w-full justify-center py-2.5">
                {status === 'loading' ? 'Resetting password…' : 'Reset password'}
              </button>
            </form>
          )}

          <p className="mt-7 text-center text-sm text-[#64748B]">
            <Link to="/login" className="font-semibold text-[#264D59] hover:underline">
              Back to sign in
            </Link>
          </p>
        </main>
      </div>
    </div>
  );
}
